import _ from 'lodash';

import { ILevelDomainData } from 'src/app/infrastructure/models/level-domain-data';
import { IRoleForm } from './role-form';
import { IRoleDto, roleDto } from 'src/assets/common/models/role-dto.g';
import { IRoleRefDto } from 'src/assets/common/models/role-ref-dto.g';
import { activityData, ActivityData, IActivityData } from '../../activity/models/activity-data';
import { functionData, FunctionData, IFunctionData } from '../../function/models/function-data';

export interface IRoleCache {
    data: IRoleData;
    url: string;
}

export const roleData = {
    requestedDomainUser_nr: 'requestedDomainUser_nr',
    domain_guid: 'domain_guid',
    guid: 'guid',
    nr: 'nr',
    name: 'name',
    normalizedName: 'normalizedName',
    // Type: false = admin role (alleen AdminPanel), true = domain role (ook aan domein-eigen functies) — zie role-type.ts.
    domainAssignable: 'domainAssignable',
    deleted: 'deleted',
    inactive: 'inactive',

    activitiesData: 'activitiesData',
    functionsData: 'functionsData'
} as const;

export type TRoleData = (typeof roleData)[keyof typeof roleData];

export interface IRoleData extends ILevelDomainData {
    [roleData.requestedDomainUser_nr]: number | null;
    [roleData.domain_guid]: string;
    
    [roleData.guid]: string;
    [roleData.nr]: number | null;
    [roleData.name]: string | null;
    [roleData.domainAssignable]: boolean;
    [roleData.deleted]: boolean;
    [roleData.inactive]: boolean;

    [roleData.activitiesData]: ActivityData[] | null;
    [roleData.functionsData]: FunctionData[] | null;
}

export class RoleData
    implements IRoleData, ILevelDomainData
{
    [roleData.requestedDomainUser_nr]: number | null = null;
    [roleData.domain_guid]!: string;
    
    [roleData.guid]!: string;
    [roleData.nr]: number | null = null;
    [roleData.name]: string | null = null;
    [roleData.domainAssignable]: boolean = false;
    [roleData.deleted]: boolean = false;
    [roleData.inactive]: boolean = false;

    [roleData.activitiesData]: ActivityData[] | null = null;
    [roleData.functionsData]: FunctionData[] | null = null;

    protected constructor(modelDto?: IRoleDto | IRoleRefDto | null);
    protected constructor(modelData: IRoleData, modelForm: IRoleForm);
    protected constructor(value?: IRoleDto | IRoleRefDto | IRoleData | null, modelForm?: IRoleForm) {
        if (!!modelForm) {
            const _modelData = value as IRoleData;
            _.assign(this, _modelData, modelForm);
        } else if (!!value) {
            const _modelDto = value as IRoleDto;
            this[roleData.guid] = _modelDto[roleDto.guid]!;
            this[roleData.nr] = _modelDto[roleDto.nr];
            this[roleData.name] = _modelDto[roleDto.name];
            this[roleData.domainAssignable] = !!_modelDto[roleDto.domainAssignable];
            this[roleData.deleted] = _modelDto[roleDto.deleted] ?? false;
            this[roleData.inactive] = _modelDto[roleDto.inactive] ?? false;

            this[roleData.activitiesData] = _modelDto[roleDto.activitiesDto]
                ?.map(item => ActivityData.create(item))
                .filter((a): a is IActivityData => a !== null && a[activityData.deleted] === false)
                .sort((a, b) => (a[activityData.name] ?? '').localeCompare(b[activityData.name] ?? ''))
                ?? null;
            this[roleData.functionsData] = _modelDto[roleDto.functionsDto]
                ?.map(item => FunctionData.create(item))
                .filter((a): a is IFunctionData => a !== null && a[functionData.deleted] === false)
                .sort((a, b) => (a[functionData.name] ?? '').localeCompare(b[functionData.name] ?? ''))
                ?? null;
        }
    }
        
    static create(modelDto?: IRoleDto | IRoleRefDto | null): IRoleData | null
    static create(modelData: IRoleData, modelForm: IRoleForm): IRoleData | null
    static create(value?: IRoleDto | IRoleRefDto | IRoleData | null, modelForm?: IRoleForm): IRoleData | null {

        if (!!modelForm) {
            const _modelData = value as IRoleData;
            return new RoleData(_modelData, modelForm);
        } else {
            const _modelDto = value as IRoleDto;
            return _modelDto ? new RoleData(_modelDto) : null;
        }
    }
}
